<template>
    <Modal ref="modalRef">
        <template #header>
            {{ $t('Add-on details') }}
        </template>

        <div class="space-y-4">
            <template v-for="(item, index) in draftRequirementItems" :key="`req-${index}`">
                <Fieldset class="grid-cols-1 md:grid-cols-2 md:gap-6 gap-4">
                    <template #legend>
                        <div class="flex items-center justify-between gap-3">
                            <h3 class="text-lg font-semibold">{{ addOnLabel }} {{ index + 1 }}</h3>

                            <Button v-if="draftRequirementItems.length > 1" severity="secondary" size="sm" @click="onRemoveItem(index)" :disabled="isSavingRequirements">
                                {{ $t('Remove') }}
                            </Button>
                        </div>
                    </template>
                    <Field v-for="field in requirementFields" :key="field.name" :error="getFieldError(index, field.name)">
                        <Label>
                            {{ getFieldDisplayLabel(field) }}
                        </Label>
                        <Input :type="field.type === 'number' ? 'number' : 'text'" :model-value="item[field.name] ?? ''" @update:model-value="(value) => onFieldChange(index, field.name, value)" :min="field.type === 'number' ? (field.min ?? null) : null" :max="field.type === 'number' ? (field.max ?? null) : null" :maxlength="field.type === 'string' ? (field.max ?? null) : null" :step="field.type === 'number' ? '0.1' : null" :placeholder="getFieldDisplayLabel(field)" />
                    </Field>
                </Fieldset>
            </template>
        </div>

        <template #footer>
            <div class="w-full flex items-center justify-between gap-3">
                <Button severity="secondary" @click="onAddAnother" :disabled="!canAddAnother || isSavingRequirements">
                    {{ $t('Add another') }}
                </Button>

                <div class="flex items-center gap-3">
                    <Button severity="secondary" @click="onClose" :disabled="isSavingRequirements">
                        {{ $t('Cancel') }}
                    </Button>

                    <Button severity="primary" @click="onSave" :loading="isSavingRequirements">
                        {{ $t('Add to cart') }}
                    </Button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import Input from '@/components/ui/input/Input.vue'
import Modal from '@/components/ui/modal/Modal.vue'
import { useToast } from '@/composables/toast/useToast.js'

const props = defineProps({
    addOnLabel: {
        type: String,
        required: true
    },
    requirementFields: {
        type: Array,
        required: true
    },
    draftRequirementItems: {
        type: Array,
        required: true
    },
    generalRequirementError: {
        type: String,
        default: ''
    },
    isSavingRequirements: {
        type: Boolean,
        default: false
    },
    getFieldError: {
        type: Function,
        required: true
    },
    onFieldChange: {
        type: Function,
        required: true
    },
    onClose: {
        type: Function,
        required: true
    },
    onSave: {
        type: Function,
        required: true
    },
    onAddAnother: {
        type: Function,
        required: true
    },
    onRemoveItem: {
        type: Function,
        required: true
    },
    canAddAnother: {
        type: Boolean,
        default: true
    }
})

const toast = useToast()

watch(
    () => props.generalRequirementError,
    (message, previousMessage) => {
        if (!message || message === previousMessage) {
            return
        }

        toast.error(message)
    }
)

const modalRef = ref(null)

const getFieldDisplayLabel = (field) => {
    const baseLabel = field?.label || field?.labelKey || field?.name
    if (!field?.suffix) {
        return baseLabel
    }

    return `${baseLabel} (${field.suffix})`
}

const openModal = () => {
    modalRef.value?.openModal()
}

const closeModal = () => {
    modalRef.value?.closeModal()
}

defineExpose({
    openModal,
    closeModal
})
</script>
